import { AsyncThunk, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { basketService, orderService } from "../../Core/Api/services";
import { AddItemToBasketRequest } from "../../Core/Api/Types/add-item-to-basket-request";
import { Basket } from "../../Core/Api/Types/basket";
import { BasketItemStatus } from "../../Core/Api/Types/basket-item-status";
import { BasketQuickviewItem } from "../../Core/Api/Types/basket-quickview-item";
import { DbOrderBasketItemInfos } from "../../Core/Api/Types/db-order-basket-item-infos";
import { DbOrderPriceRuleInfos } from "../../Core/Api/Types/db-order-price-rule-infos";
import { DbOrderProductInfos } from "../../Core/Api/Types/db-order-product-infos";
import { OrderProductType } from "../../Core/Api/Types/order-product-type";
import { TranskodeBundleAsset } from "../../Core/Api/Types/transkode-bundle-asset";
import { TranskodeBundleComponent } from "../../Core/Api/Types/transkode-bundle-component";
import { TranskodeGenericOptionsProfile } from "../../Core/Api/Types/transkode-generic-options-profile";
import { TranskodeWatermarkVendor } from "../../Core/Api/Types/transkode-watermark-vendor";
import { TranskodeBundleComponentType } from "../../Core/Api/Types/transkode-bundle-component-type";
import { XmlDeliveryIdentityProfilesInfo } from "../../Core/Api/Types/xml-delivery-identity-profiles-info";
import { XmlBundleDeliveryIdentityProfilesInfo } from "../../Core/Api/Types/xml-bundle-delivery-identity-profiles-info";
import { GetBasketQuickviewResponse } from "../../Core/Api/Types/get-basket-quickview-response";

export interface BasketPriceRuleStore {
  itemId: number;
  rules: DbOrderPriceRuleInfos[];
}

export interface ReduxStateBasket {
  // Panel
  items: BasketQuickviewItem[];
  request: AddItemToBasketRequest[];
  isLoaded: boolean;
  message: string | null;
  // Basket main frame
  basket: Basket | null;
  profiles: XmlDeliveryIdentityProfilesInfo[] | null;
  bundleProfiles: XmlBundleDeliveryIdentityProfilesInfo[] | null;
  basketPriceRules: BasketPriceRuleStore[];
  basketSelectedItemIds: number[];
  basketSelectedGroupId: number | undefined;
  basketShowOnlyAvailable: boolean;
  basketUnitApiCallLoading: boolean;
  lastPathBeforeBasket: string;
}

const initialState: ReduxStateBasket = {
  items: [],
  request: [],
  isLoaded: false,
  message: null,
  basket: null,
  profiles: null,
  bundleProfiles: null,
  basketPriceRules: [],
  basketSelectedItemIds: [],
  basketSelectedGroupId: undefined,
  basketShowOnlyAvailable: true,
  basketUnitApiCallLoading: false,
  lastPathBeforeBasket: "",
};

export interface ReduxUpdateProductSettings {
  itemId: number;
  settings: any;
}

export interface ReduxUpdateProductSettingsTgop {
  itemId: number;
  field: TgopField;
  value: any;
}

export interface ReduxUpdateProductSettingsRoot {
  itemId: number;
  field: SettingRootField;
  value: any;
}

export interface ReduxUpdateProductComponentSettingsRoot {
  itemId: number;
  field: TgopField;
  value: any;
  type: TranskodeBundleComponentType;
}

export interface ReduxUpdateProductRoot {
  itemId: number;
  field: ProductField;
  value: any;
}

export interface ReduxUpdateItemStatus {
  itemId: number;
  status: BasketItemStatus;
}

export interface ReduxUpdateItemRoot {
  itemId: number;
  field: ItemField;
  value: any;
}

export enum ReduxUpdateItemManualTaskField {
  Disabled,
  Prices,
}

export interface ReduxUpdateItemManualTask {
  itemId: number;
  jobIndex: number;
  taskIndex: number;
  field: ReduxUpdateItemManualTaskField;
  value: any;
}

export interface ReduxUpdateItemManualTaskFormField {
  itemId: number;
  fieldIndex: number;
  value: any;
}

export interface ReduxApplyToAll {
  itemId: number;
}

// BundleMaker payloads
export interface ReduxBundleMakerAddComponent {
  itemId: number;
  component: TranskodeBundleComponent;
}

export interface ReduxBundleMakerAddComponentAsset {
  itemId: number;
  componentIndex: number;
  asset: TranskodeBundleAsset;
}

export interface ReduxBundleMakerRemoveComponentAsset {
  itemId: number;
  componentIndex: number;
  assetIndex: number;
}

export interface ReduxBundleMakerRemoveComponent {
  itemId: number;
  componentIndex: number;
}

export enum TgopField {
  FileFormat,
  Fps,
  ScanType,
  AudioMixes,
  AudioAvoidVarispeed,
  Picture,
  BurnTimecode,
  VideoFilterDesature,
  SubtitleBurned,
  SubtitleSidecarFiles,
  SubtitleSidecarFormat,
  OverlayText,
  AudioOnly,
  TimecodeTrim,
  MultipleTimecodeTrims,
  DubbingCard,
  TextlessMaterial,
  RestorationCard,
  ImageSidecarFiles,
  CustomEdl,
  DcpCpl,
  ExplodeAudioChannels,
  RewrapOnly,
  DropFrame,
  DCP1FilePerReel,
}

export enum SettingRootField {
  FileFormat,
  Fps,
  ScanType,
  BurnTimecode,
  VideoFilterDesature,
  SubtitleBurned,
  OverlayText,
  AudioOnly,
  AntipiracyTerritories,
  AntipiracyLabels,
  AntipiracyCustomId,
  AntipiracyTitle,
  AntipiracyPolicyName,
  AntipiracyDeliveryMode,
  AntipiracyEnableService,
  AntipiracyReferenceAssetId,
  AntipicaryCutStartMedia,
  OverrideAudioMixMappingId,
  AudioMixMappingId,
  BroadcasterId,
  VideoProfile,
  TargetFeatureVideoProfile,
  Countries,
  Components,
  Metadata,
  SubProjectCustomerId,
  SubProjectTechUserId,
  SubProjectBroadcasterId,
  SubProjectDueDate,
  SubProjectInLang,
  SubProjectOutLang,
  SubProjectGeneralType,
  SubProjectStandardType,
  SubProjectSubMode,
  SubProjectDestContent,
  SubProjectType,
  SubProjectStartTimecode,
  SubProjectDuration,
  SubProjectSeparatedPartialFile,
  SubProjectSeparatedPartialFileFormat,
  ForceNoDubbingCardUse,
  ForceNoTextlessUse,
  ForceNoRestorationCardUse,
  IsProxy,
  ImfCpl,
  DcpCpl,
  ManualTaskQuote,
  ExplodeAudioChannels,
  RewrapOnly,
  TimecodeTrim,
  MultipleTimecodeTrims,
  CustomEdl,
  DropFrame,
  YoutubeDeliveryChannel,
  YoutubeDeliveryCustomId,
  YoutubeDeliveryTitle,
  YoutubeDeliveryDescription,
  YoutubeDeliveryMovieLabels,
  YoutubeDeliveryKeywords,
  YoutubeDeliveryStartTime,
  YoutubeDeliveryEndTime,
  YoutubeDeliveryNotifySubscribers,
  YoutubeDeliveryTerritoriesCode,
  YoutubeDeliveryBlockOutsideOwnership,
  YoutubeDeliveryUsagePolicy,
  YoutubeDeliveryPlaylistIds,
  YoutubeDeliveryIsMadeForKids,
  YoutubeDeliveryAdBreakTimes,
  YoutubeDeliveryCustomThumbnail,
  YoutubeDeliveryThumbnailFileId,
  ArchiveHeritageTape,
  YoutubeDeliveryPreRoll,
  YoutubeDeliveryPostRoll,
  ObjectId,
}

export enum ProductField {
  WatermarkTextTemplate,
  WatermarkProcessingVendor,
  GeneratedAssetVisible,
  DeliveryMethod,
  DeliveryStorageId,
  Comment,
  Emails,
  TransferMediaAnnexFileIds,
  Price,
  Proxy,
}

export enum ItemField {
  Price,
  PriceOverride,
  CustomerOverride,
  ContractDealOverride,
  ContractCustomerOverride,
  AttachmentTransfer,
  BillingEmails,
  ManualTaskTemplateId,
  ManualTaskTemplate,
  ScheduleTime,
  DeliveryAttachment,
}

interface AddItemToBasketResponseRedux {
  items: DbOrderBasketItemInfos[] | null;
  quickview: GetBasketQuickviewResponse | null;
}
export const RemoveItem: any = createAsyncThunk("basket/RemoveItemFromBasket", async (itemId: number) => {
  const response = await basketService.RemoveItemFromBasket(itemId);
  return response;
});

export const AddItem: AsyncThunk<AddItemToBasketResponseRedux, AddItemToBasketRequest, {}> = createAsyncThunk(
  "basket/AddItemToBasket",
  async (request: AddItemToBasketRequest) => {
    const response = await basketService.AddItemToBasket(request);

    if (!response.success) throw new Error(response.message || "Error while adding items to basket."); // Don't refetch basket

    const quickview = await basketService.GetBasketQuickView();

    const ret: AddItemToBasketResponseRedux = { items: response.items, quickview: quickview };

    return ret;
  }
);

export const AddItems: AsyncThunk<AddItemToBasketResponseRedux, AddItemToBasketRequest[], {}> = createAsyncThunk(
  "basket/AddItemsToBasket",
  async (request: AddItemToBasketRequest[]) => {
    const response = await basketService.AddItemsToBasket(request);

    if (!response.success) throw new Error(response.message || "Error while adding items to basket."); // Don't refetch basket

    const quickview = await basketService.GetBasketQuickView();

    const ret: AddItemToBasketResponseRedux = { items: response.items, quickview: quickview };

    return ret;
  }
);

export const LoadProfiles: any = createAsyncThunk("basket/GetBasketDeliveryIdentityProfiles", async () => {
  const response = await basketService.GetBasketDeliveryIdentityProfiles();

  if (!response.success) throw new Error(response.message || "Error while loading profiles.");

  return response;
});

export const AddPreconfiguredItemDCPReelsToBasket: any = createAsyncThunk(
  "basket/AddPreconfiguredItemDCPReelsToBasket",
  async (request: AddItemToBasketRequest) => {
    let add = await basketService.AddPreconfiguredItemDCPReelsToBasket(request);
    let load = await basketService.GetBasketQuickView();

    return { get: load, add: add };
  }
);

export const AddPreconfiguredItemDCPOVToBasket: any = createAsyncThunk(
  "basket/AddPreconfiguredItemDCPOVToBasket",
  async (request: AddItemToBasketRequest) => {
    let add = await basketService.AddPreconfiguredItemDCPOVToBasket(request);
    let load = await basketService.GetBasketQuickView();

    return { get: load, add: add };
  }
);

export const GetQuickview: any = createAsyncThunk("basket/GetQuickview", async () => {
  const response = await basketService.GetBasketQuickView();
  return response;
});

export const ModifyItem: any = createAsyncThunk("basket/ModifyItemToBasket", async (itemId: number) => {
  let response = await orderService.ModifyOrderItem(itemId);
  let load = await basketService.GetBasketQuickView();

  return { success: response.success, message: response.message, items: load.basket?.items };
});

export const CopyItem: any = createAsyncThunk("basket/CopyItemToBasket", async (itemId: number) => {
  let response = await orderService.CopyOrderItemToBasket(itemId);
  let load = await basketService.GetBasketQuickView();

  return { success: response.success, message: response.message, items: load.basket?.items };
});

export const DuplicateItem: any = createAsyncThunk("basket/DuplicateBasketItem", async (itemId: number) => {
  const response = await basketService.DuplicateItem(itemId);
  return { originalItemId: itemId, copiedItemId: response.objectId };
});

const basketSlice = createSlice({
  name: "basket",
  initialState,

  reducers: {
    ResetBasket: (state) => {
      state.items = [];
      state.basket = null;
    },
    SetLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
    SetLastPath: (state, action) => {
      state.lastPathBeforeBasket = action.payload;
    },
    StoreInitialState: (state, action) => {
      state.items = action.payload;
      console.log("basket:StoreInitialState() ok");
    },
    StoreBasket: (state, action) => {
      state.basket = action.payload;
      console.log("basket:StoreBasket() ok");
    },
    StoreBasketPriceRule: (state, action: PayloadAction<BasketPriceRuleStore>) => {
      let index = state.basketPriceRules.findIndex((x) => x.itemId === action.payload.itemId);

      if (index === -1) state.basketPriceRules.push(action.payload);
      else state.basketPriceRules[index] = action.payload;

      console.log("basket:StoreBasketPriceRule() ok with index = " + index);
    },
    SelectUnSelectGroupOnBasket: (state, action: PayloadAction<number | undefined>) => {
      state.basketSelectedGroupId = action.payload;
      console.log("basket:SelectUnSelectGroupOnBasket(" + action.payload + ") ok");
    },
    SelectItemOnBasket: (state, action: PayloadAction<number>) => {
      if (state.basketSelectedItemIds.includes(action.payload)) return;

      state.basketSelectedItemIds.push(action.payload);
      console.log("basket:SelectItemOnBasket(" + action.payload + ") ok");
    },
    SelectItemsOnBasket: (state, action: PayloadAction<number[]>) => {
      action.payload.forEach((id) => {
        if (state.basketSelectedItemIds.includes(id)) return;

        state.basketSelectedItemIds.push(id);
        console.log("basket:SelectItemsOnBasket(" + id + ") ok");
      });
    },
    UnselectItemOnBasket: (state, action: PayloadAction<number>) => {
      state.basketSelectedItemIds = state.basketSelectedItemIds.filter((x) => x !== action.payload);
      console.log("basket:UnselectItemOnBasket(" + action.payload + ") ok");
    },
    UnselectItemsOnBasket: (state, action: PayloadAction<number[]>) => {
      state.basketSelectedItemIds = state.basketSelectedItemIds.filter((x) => !action.payload.includes(x));
      console.log("basket:UnselectItemsOnBasket(" + action.payload + ") ok");
    },
    UnselectAllItemsOnBasket: (state) => {
      state.basketSelectedItemIds = [];
      console.log("basket:UnselectAllItemsOnBasket() ok");
    },
    UpdateItemOnBasket: (state, action: PayloadAction<DbOrderBasketItemInfos>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.id);
      state.basket!.items![index] = action.payload;
      console.log("basket:UpdateItemOnBasket(" + action.payload.id + ") ok");
    },
    UpdateItemSettingsOnBasket: (state, action: PayloadAction<ReduxUpdateProductSettings>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (!state.basket!.items![index].selectedProduct) return;

      state.basket!.items![index].selectedProduct!.settings = action.payload.settings;
      console.log("basket:UpdateItemSettingsOnBasket(" + action.payload.itemId + ") ok");
    },
    UpdateItemSettingsTgopOnBasket: (state, action: PayloadAction<ReduxUpdateProductSettingsTgop>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (!state.basket!.items![index].selectedProduct || !state.basket!.items![index].selectedProduct?.settings)
        return;

      const tgop: TranskodeGenericOptionsProfile = state.basket!.items![index].selectedProduct!.settings.tgop;

      switch (action.payload.field) {
        case TgopField.FileFormat:
          tgop.fileFormat!.selectedValue = action.payload.value;
          break;
        case TgopField.Fps:
          tgop.frameRate!.selectedValue = action.payload.value;
          break;
        case TgopField.DropFrame:
          tgop.frameRate!.selectedValue!.dropFrame = action.payload.value;
          break;
        case TgopField.ScanType:
          tgop.videoFieldOrder!.selectedValue = action.payload.value;
          break;
        case TgopField.AudioMixes:
          tgop.audioMixes!.selectedValues = action.payload.value;
          break;
        case TgopField.Picture:
          tgop.picture!.selectedValue = action.payload.value;
          break;
        case TgopField.AudioAvoidVarispeed:
          tgop.avoidVarispeed = !tgop.avoidVarispeed;
          break;
        case TgopField.BurnTimecode:
          tgop.burnTimecode = !tgop.burnTimecode;
          break;
        case TgopField.AudioOnly:
          tgop.isAudioOnly = !tgop.isAudioOnly;
          break;
        case TgopField.ExplodeAudioChannels:
          tgop.explodeAudioChannels = !tgop.explodeAudioChannels;
          break;
        case TgopField.RewrapOnly:
          tgop.rewrapOnly = !tgop.rewrapOnly;
          break;
        case TgopField.VideoFilterDesature:
          if (tgop.videoFilters) tgop.videoFilters = null;
          else tgop.videoFilters = { desaturation: true };
          break;
        case TgopField.SubtitleBurned:
          tgop.subtitleBurned!.selectedValue = action.payload.value;
          break;
        case TgopField.SubtitleSidecarFiles:
          let isMulti = Array.isArray(action.payload.value);
          let sdh: boolean = action.payload.value.sdh as boolean;

          if (!isMulti && action.payload.value.value === null) {
            // Remove case
            let index = tgop.subtitleSidecarFiles!.selectedValues!.findIndex((x) => x.isSDH === sdh);

            if (index !== -1) tgop.subtitleSidecarFiles!.selectedValues!.splice(index, 1);

            return;
          }

          if (isMulti && action.payload.value.value === null) {
            // Remove case
            if (tgop.subtitleSidecarFiles!.selectedValues)
              tgop.subtitleSidecarFiles!.selectedValues = tgop.subtitleSidecarFiles!.selectedValues?.filter(
                (x) => x.isSDH !== sdh
              );

            return;
          }

          if (isMulti) tgop.subtitleSidecarFiles!.selectedValues = action.payload.value;
          else {
            // Case of byprofile page where value is sdh/non-sdh cardinality 1
            if (tgop.subtitleSidecarFiles!.selectedValues)
              tgop.subtitleSidecarFiles!.selectedValues = tgop.subtitleSidecarFiles!.selectedValues?.filter(
                (x) => x.isSDH !== sdh
              );
            tgop.subtitleSidecarFiles!.selectedValues?.push(action.payload.value.value);
          }
          break;

        case TgopField.SubtitleSidecarFormat:
          tgop.fileFormatSubtitleSidecar!.selectedValue = action.payload.value;
          break;
        case TgopField.OverlayText:
          tgop.overlayTextPlaceholder = action.payload.value;
          break;
        case TgopField.TimecodeTrim:
          tgop.timecodeTrim = action.payload.value;
          break;
        case TgopField.MultipleTimecodeTrims:
          tgop.listTimecodeTrims = action.payload.value;
          break;
        case TgopField.CustomEdl:
          tgop.editAndConcatCustomEdl = action.payload.value;
          break;
        case TgopField.DubbingCard:
          tgop.dubCards!.selectedValues = action.payload.value;
          break;
      }

      console.log(
        "basket:UpdateItemSettingsTgopOnBasket(" + action.payload.itemId + ", " + action.payload.field + ") ok"
      );
    },
    UpdateItemSettingsImfOnBasket: (state, action: PayloadAction<ReduxUpdateProductSettingsTgop>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (!state.basket!.items![index].selectedProduct || !state.basket!.items![index].selectedProduct?.settings)
        return;

      const tgop: TranskodeGenericOptionsProfile =
        state.basket!.items![index].selectedProduct!.settings.cpls.selectedValue;

      switch (action.payload.field) {
        case TgopField.FileFormat:
          tgop.fileFormat!.selectedValue = action.payload.value;
          break;
        case TgopField.Fps:
          tgop.frameRate!.selectedValue = action.payload.value;
          break;
        case TgopField.ScanType:
          tgop.videoFieldOrder!.selectedValue = action.payload.value;
          break;
        case TgopField.BurnTimecode:
          tgop.burnTimecode = !tgop.burnTimecode;
          break;
        case TgopField.AudioOnly:
          tgop.isAudioOnly = !tgop.isAudioOnly;
          break;
        case TgopField.OverlayText:
          tgop.overlayTextPlaceholder = action.payload.value;
          break;
        case TgopField.TimecodeTrim:
          tgop.timecodeTrim = action.payload.value;
          break;
        case TgopField.MultipleTimecodeTrims:
          tgop.listTimecodeTrims = action.payload.value;
          break;
        case TgopField.DCP1FilePerReel:
          state.basket!.items![index].selectedProduct!.settings.oneFilePerReel =
            !state.basket!.items![index].selectedProduct!.settings.oneFilePerReel;
          break;
      }

      console.log(
        "basket:UpdateItemSettingsDcpImfOnBasket(" + action.payload.itemId + ", " + action.payload.field + ") ok"
      );
    },
    UpdateItemSettingsRootOnBasket: (state, action: PayloadAction<ReduxUpdateProductSettingsRoot>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (!state.basket!.items![index].selectedProduct || !state.basket!.items![index].selectedProduct?.settings)
        return;

      const settings = state.basket!.items![index].selectedProduct!.settings;

      switch (action.payload.field) {
        case SettingRootField.FileFormat:
          settings.fileFormat!.selectedValue = action.payload.value;
          break;
        case SettingRootField.Fps:
          settings.frameRate!.selectedValue = action.payload.value;
          break;
        case SettingRootField.DropFrame:
          settings.frameRate!.selectedValue.dropFrame = action.payload.value;
          break;
        case SettingRootField.ScanType:
          settings.videoFieldOrder!.selectedValue = action.payload.value;
          break;
        case SettingRootField.BurnTimecode:
          settings.burnTimecode = !settings.burnTimecode;
          break;
        case SettingRootField.AudioOnly:
          settings.isAudioOnly = !settings.isAudioOnly;
          break;
        case SettingRootField.ExplodeAudioChannels:
          settings.explodeAudioChannels = !settings.explodeAudioChannels;
          break;
        case SettingRootField.RewrapOnly:
          settings.rewrapOnly = !settings.rewrapOnly;
          break;
        case SettingRootField.VideoFilterDesature:
          if (settings.videoFilters) settings.videoFilters = null;
          else settings.videoFilters = { desaturation: true };
          break;
        case SettingRootField.OverlayText:
          settings.overlayTextPlaceholder = action.payload.value;
          break;
        case SettingRootField.AntipiracyCustomId:
          settings.customId = action.payload.value;
          break;
        case SettingRootField.AntipiracyDeliveryMode:
          settings.deliveryMode = action.payload.value;
          break;
        case SettingRootField.AntipiracyLabels:
          settings.movieLabels = action.payload.value;
          break;
        case SettingRootField.AntipiracyTerritories:
          settings.territoriesCodes = action.payload.value;
          break;
        case SettingRootField.AntipiracyPolicyName:
          settings.policyName = action.payload.value;
          break;
        case SettingRootField.AntipiracyTitle:
          settings.title = action.payload.value;
          break;
        case SettingRootField.AntipiracyEnableService:
          settings.enableService = action.payload.value;
          break;
        case SettingRootField.AntipiracyReferenceAssetId:
          settings.referenceAssetId = action.payload.value;
          break;
        case SettingRootField.OverrideAudioMixMappingId:
          settings.overrideAudioMixMappingId = action.payload.value;
          break;
        case SettingRootField.AudioMixMappingId:
          settings.audioMixMappingId = action.payload.value;
          break;
        case SettingRootField.BroadcasterId:
          settings.externalBroadcasterId = action.payload.value;
          break;
        case SettingRootField.VideoProfile:
          settings.videoProfile = action.payload.value;
          break;
        case SettingRootField.TargetFeatureVideoProfile:
          settings.targetFeatureVideoProfile = action.payload.value;
          break;
        case SettingRootField.Countries:
          settings.countries = action.payload.value;
          break;
        case SettingRootField.Components:
          settings.components = action.payload.value;
          break;
        case SettingRootField.Metadata:
          settings.metadata = action.payload.value;
          break;
        case SettingRootField.SubProjectCustomerId:
          settings.customerId = action.payload.value;
          break;
        case SettingRootField.SubProjectTechUserId:
          settings.techUserId = action.payload.value;
          break;
        case SettingRootField.SubProjectBroadcasterId:
          settings.broadcasterId = action.payload.value;
          break;
        case SettingRootField.SubProjectDueDate:
          settings.dueDate = action.payload.value;
          break;
        case SettingRootField.SubProjectInLang:
          settings.originLanguage = action.payload.value;
          break;
        case SettingRootField.SubProjectOutLang:
          settings.outputLanguage = action.payload.value;
          break;
        case SettingRootField.SubProjectGeneralType:
          settings.generalType = action.payload.value;
          break;
        case SettingRootField.SubProjectStandardType:
          settings.standardType = action.payload.value;
          break;
        case SettingRootField.SubProjectSubMode:
          settings.mode = action.payload.value;
          break;
        case SettingRootField.SubProjectDestContent:
          settings.contentType = action.payload.value;
          break;
        case SettingRootField.SubProjectType:
          settings.type = action.payload.value;
          break;
        case SettingRootField.SubProjectStartTimecode:
          settings.timecodeInCue = action.payload.value;
          break;
        case SettingRootField.SubProjectDuration:
          settings.durationMin = action.payload.value;
          break;
        case SettingRootField.SubProjectSeparatedPartialFile:
          settings.separatedPartialFile = action.payload.value;
          break;
        case SettingRootField.SubProjectSeparatedPartialFileFormat:
          settings.fileFormatSeparatedPartial.selectedValue = action.payload.value;
          break;
        case SettingRootField.ForceNoDubbingCardUse:
          settings.overrideDoNotUseDubCards = action.payload.value;
          break;
        case SettingRootField.ForceNoTextlessUse:
          settings.overrideDoNotUseTextless = action.payload.value;
          break;
        case SettingRootField.ForceNoRestorationCardUse:
          settings.overrideDoNotUseRestorationCard = action.payload.value;
          break;
        case SettingRootField.IsProxy:
          settings.isProxy = action.payload.value;
          break;
        case SettingRootField.DcpCpl:
        case SettingRootField.ImfCpl:
          settings.cpls.selectedValue = action.payload.value;
          break;
        case SettingRootField.AntipicaryCutStartMedia:
          settings.cutStartMedia = action.payload.value;
          break;
        case SettingRootField.ManualTaskQuote:
          settings.quote = action.payload.value;
          break;
        case SettingRootField.TimecodeTrim:
          settings.timecodeTrim = action.payload.value;
          break;
        case SettingRootField.MultipleTimecodeTrims:
          settings.listTimecodeTrims = action.payload.value;
          break;
        case SettingRootField.CustomEdl:
          settings.editAndConcatCustomEdl = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryChannel:
          settings.channel = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryCustomId:
          settings.customId = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryTitle:
          settings.title = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryDescription:
          settings.description = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryMovieLabels:
          settings.movieLabels = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryKeywords:
          settings.keywords = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryStartTime:
          settings.startTime = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryEndTime:
          settings.endTime = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryTerritoriesCode:
          settings.territoriesCodes = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryNotifySubscribers:
          settings.notifySubscribers = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryBlockOutsideOwnership:
          settings.blockOutsideOwnership = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryUsagePolicy:
          settings.usagePolicy = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryPlaylistIds:
          settings.playlistIds = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryIsMadeForKids:
          settings.isMadeForKids = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryAdBreakTimes:
          settings.adBreakTimes = action.payload.value;
          break;
        case SettingRootField.ArchiveHeritageTape:
          let subAsset = settings.assets.find((x) => x.assetId === action.payload.value);
          subAsset.isSelected = !subAsset.isSelected;
          break;
        case SettingRootField.YoutubeDeliveryCustomThumbnail:
          settings.customThumbnail = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryThumbnailFileId:
          settings.thumbnailFileId = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryPreRoll:
          settings.preRoll = action.payload.value;
          break;
        case SettingRootField.YoutubeDeliveryPostRoll:
          settings.postRoll = action.payload.value;
          break;
        case SettingRootField.ObjectId:
          settings.objectId = action.payload.value;
      }

      console.log(
        "basket:UpdateItemSettingsRootOnBasket(" + action.payload.itemId + ", " + action.payload.field + ") ok"
      );
    },
    UpdateItemProductRootOnBasket: (state, action: PayloadAction<ReduxUpdateProductRoot>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (!state.basket!.items![index].selectedProduct) return;

      const product: DbOrderProductInfos = state.basket!.items![index].selectedProduct!;

      switch (action.payload.field) {
        case ProductField.WatermarkTextTemplate:
          product.watermarkTemplate = action.payload.value;
          break;
        case ProductField.WatermarkProcessingVendor:
          product.watermarkSecurity =
            product.watermarkSecurity === null ? TranskodeWatermarkVendor.NexguardCivolution : null;
          break;
        case ProductField.GeneratedAssetVisible:
          product.generatedAssetsVisible = !product.generatedAssetsVisible;
          break;
        case ProductField.DeliveryMethod:
          product.deliveryMethod = action.payload.value;
          break;
        case ProductField.DeliveryStorageId:
          product.deliveryStorageId = action.payload.value;
          break;
        case ProductField.Comment:
          product.comment = action.payload.value;
          break;
        case ProductField.Emails:
          if (action.payload.value.every((element) => product.emailNotificationList?.includes(element))) {
            product.emailNotificationList = action.payload.value;
          } else {
            if (
              product.emailNotificationList === null ||
              product.emailNotificationList.length === 0 ||
              action.payload.value.length === 0
            )
              product.emailNotificationList = action.payload.value;
            else {
              product.emailNotificationList = product.emailNotificationList!.concat(action.payload.value);
              let values = product.emailNotificationList!.concat(action.payload.value);
              product.emailNotificationList = values.filter((x, i) => values.indexOf(x) === i);
            }
          }

          break;
        case ProductField.TransferMediaAnnexFileIds:
          product.transferMediaAnnexFileIds = action.payload.value;
          break;
        case ProductField.Price:
          product.price = action.payload.value;
          break;
        case ProductField.Proxy:
          product.proxy = !product.proxy;
          break;
      }

      console.log(
        "basket:UpdateItemProductRootOnBasket(" + action.payload.itemId + ", " + action.payload.field + ") ok"
      );
    },
    UpdateItemStatusOnBasket: (state, action: PayloadAction<ReduxUpdateItemStatus>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);

      if (action.payload.status !== BasketItemStatus.Loading && !state.basket!.items![index].selectedProduct) return;

      state.basket!.items![index].status = action.payload.status;
      console.log("basket:UpdateItemStatusOnBasket(" + action.payload.itemId + ", " + action.payload.status + ") ok");
    },
    UpdateItemRootOnBasket: (state, action: PayloadAction<ReduxUpdateItemRoot>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);
      let item = state.basket!.items![index];

      switch (action.payload.field) {
        case ItemField.Price:
          item.totalPrice = action.payload.value;
          break;
        case ItemField.PriceOverride:
          item.overridenPrice = action.payload.value;
          break;
        case ItemField.ContractDealOverride:
          item.contractExternalDealId = action.payload.value;
          break;
        case ItemField.ContractCustomerOverride:
          item.contractExternalCustomerId = action.payload.value;
          break;
        case ItemField.CustomerOverride:
          item.customerIdOverride = action.payload.value;
          break;
        case ItemField.AttachmentTransfer:
          item.fileTransferId = action.payload.value;
          break;
        case ItemField.DeliveryAttachment:
          item.deliveryFileTransferId = action.payload.value;
          break;
        case ItemField.BillingEmails:
          item.billingEmails = action.payload.value;
          break;
        case ItemField.ManualTaskTemplateId:
          item.manualTasksTemplateId = action.payload.value;
          break;
        case ItemField.ManualTaskTemplate:
          item.manualTasksTemplate = action.payload.value;
          break;
        case ItemField.ScheduleTime:
          item.scheduleTime = action.payload.value;
          break;
      }

      console.log("basket:UpdateItemRootOnBasket(" + action.payload.itemId + ", " + action.payload.field + ") ok");
    },
    UpdateItemManualTaskOnBasket: (state, action: PayloadAction<ReduxUpdateItemManualTask>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);
      let item = state.basket!.items![index];

      if (!item.manualTasksTemplate) return;

      switch (action.payload.field) {
        case ReduxUpdateItemManualTaskField.Disabled:
          item.manualTasksTemplate.jobs![action.payload.jobIndex].tasks![action.payload.taskIndex].isDisabled =
            action.payload.value;
          break;
        case ReduxUpdateItemManualTaskField.Prices:
          item.manualTasksTemplate.jobs![action.payload.jobIndex].tasks![action.payload.taskIndex].prices =
            action.payload.value;
          break;
      }

      console.log(
        "basket:UpdateItemManualTaskOnBasket(" +
          action.payload.itemId +
          ", j=" +
          action.payload.jobIndex +
          ", t=" +
          action.payload.taskIndex +
          ") ok"
      );
    },
    UpdateItemManualTaskFormFieldOnBasket: (state, action: PayloadAction<ReduxUpdateItemManualTaskFormField>) => {
      if (!state.basket || !state.basket.items) return;

      let index = state.basket!.items!.findIndex((x) => x.id === action.payload.itemId);
      let item = state.basket!.items![index];

      if (
        !item.manualTasksTemplate ||
        !item.manualTasksTemplate.formFields ||
        item.manualTasksTemplate.formFields.length === 0
      )
        return;

      item.manualTasksTemplate.formFields![action.payload.fieldIndex].values = action.payload.value;

      console.log(
        "basket:UpdateItemManualTaskFormFieldOnBasket(" +
          action.payload.itemId +
          ", i=" +
          action.payload.fieldIndex +
          ") ok"
      );
    },
    ApplyToAllOnBasket: (state, action: PayloadAction<ReduxApplyToAll>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      state.basket.items
        .filter((x) => x.selectedProduct && x.selectedProduct.type === item?.selectedProduct?.type)
        .forEach((otherItem) => {
          switch (otherItem.selectedProduct?.type) {
            case OrderProductType.FaspexSend:
              otherItem.selectedProduct.comment = item?.selectedProduct?.comment!;
              otherItem.selectedProduct.emailNotificationList = item?.selectedProduct?.emailNotificationList!;
              otherItem.selectedProduct.deliveryMethod = item?.selectedProduct?.deliveryMethod!;
              otherItem.selectedProduct.deliveryStorageId = item?.selectedProduct?.deliveryStorageId!;
              break;
          }
        });

      console.log("basket:ApplyToAllOnBasket(" + action.payload.itemId + ") ok");
    },
    SetPanelMessage: (state, action) => {
      state.message = action.payload;
      console.log("basket:SetPanelMessage() ok");
    },
    ShowOnlyAvailableToggleBasket: (state) => {
      state.basketShowOnlyAvailable = !state.basketShowOnlyAvailable;
      console.log("basket:ShowOnlyAvailableToggle() ok");
    },
    SetBasketUnitApiCallLoading: (state, action: PayloadAction<boolean>) => {
      state.basketUnitApiCallLoading = action.payload;
      console.log("basket:SetBasketUnitApiCallLoading(" + action.payload + ") ok");
    },
    /*
    BundleMaker actions
    */
    BundleMakerAddComponent: (state, action: PayloadAction<ReduxBundleMakerAddComponent>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      item.selectedProduct.settings.components.push(action.payload.component);
      console.log("basket:BundleMakerAddComponent() ok");
    },
    BundleMakerRemoveComponent: (state, action: PayloadAction<ReduxBundleMakerRemoveComponent>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      item.selectedProduct.settings.components.splice(action.payload.componentIndex, 1);
      console.log("basket:BundleMakerRemoveComponent() ok");
    },
    BundleMakerAddComponentAsset: (state, action: PayloadAction<ReduxBundleMakerAddComponentAsset>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      item.selectedProduct.settings.components[action.payload.componentIndex].assets.push(action.payload.asset);
      console.log("basket:BundleMakerAddComponentAsset() ok");
    },
    BundleMakerRemoveComponentAsset: (state, action: PayloadAction<ReduxBundleMakerRemoveComponentAsset>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      item.selectedProduct.settings.components[action.payload.componentIndex].assets.splice(
        action.payload.assetIndex,
        1
      );
      console.log("basket:BundleMakerRemoveComponentAsset() ok");
    },
    BundleMakerRemoveComponentAssetAll: (state, action: PayloadAction<ReduxBundleMakerRemoveComponentAsset>) => {
      if (!state.basket || !state.basket.items) return;

      let item = state.basket!.items!.find((x) => x.id === action.payload.itemId);

      if (!item || !item.selectedProduct) return;

      item.selectedProduct.settings.components[action.payload.componentIndex].assets = [];
      console.log("basket:BundleMakerRemoveComponentAssetAll() ok");
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(RemoveItem.fulfilled, (state, action) => {
      state.items = state.items.filter((x) => x.id !== action.payload.objectId);

      if (state.basket && state.basket.items)
        state.basket.items = state.basket.items.filter((x) => x.id !== action.payload.objectId);

      console.log("basket:RemoveItem(" + action.payload.objectId + ") ok");
    });
    builder.addCase(AddItem.fulfilled, (state, action) => {
      if (state.basket && state.basket.items && action.payload.items)
        for (let i = 0; i < action.payload.items.length; i++) state.basket.items.push(action.payload.items[i]);

      state.items = action.payload.quickview?.basket?.items!;

      console.log("basket:AddItem() ok");
    });
    builder.addCase(AddItems.fulfilled, (state, action) => {
      if (state.basket && state.basket.items && action.payload.items)
        for (let i = 0; i < action.payload.items.length; i++) state.basket.items.push(action.payload.items[i]);

      state.items = action.payload.quickview?.basket?.items!;

      console.log("basket:AddItems() ok");
    });
    builder.addCase(LoadProfiles.fulfilled, (state, action) => {
      state.profiles = action.payload.profilesInfos;
      state.bundleProfiles = action.payload.bundleProfilesInfos;
      console.log("basket:LoadProfiles ok");
    });
    builder.addCase(AddPreconfiguredItemDCPReelsToBasket.fulfilled, (state, action) => {
      state.items = action.payload.get.basket?.items!;
      console.log("basket:AddPreconfiguredItemDCPReelsToBasket(" + action.payload.add.objectId + ") ok");
    });
    builder.addCase(AddPreconfiguredItemDCPOVToBasket.fulfilled, (state, action) => {
      state.items = action.payload.get.basket?.items!;
      console.log("basket:AddPreconfiguredItemDCPOVToBasket(" + action.payload.add.objectId + ") ok");
    });
    builder.addCase(ModifyItem.fulfilled, (state, action) => {
      if (action.payload.items) state.items = action.payload.items;

      console.log("basket:ModifyItem() ok");
    });
    builder.addCase(CopyItem.fulfilled, (state, action) => {
      if (action.payload.items) state.items = action.payload.items;

      console.log("basket:CopyItem() ok");
    });
    builder.addCase(DuplicateItem.fulfilled, (state, action) => {
      let itemCopy = Object.assign(
        {},
        state.basket?.items?.find((x) => x.id === action.payload.originalItemId)
      );
      itemCopy.id = action.payload.copiedItemId!;

      state.basket?.items?.push(itemCopy);
      console.log("basket:DuplicateItem(from " + action.payload.originalItemId + " to " + itemCopy.id + ") ok");
    });
    builder.addCase(GetQuickview.fulfilled, (state, action) => {
      state.items = state.items;
      console.log("basket:GetQuickview() ok");
    });
  },
});

export const {
  ResetBasket,
  SetLoaded,
  SetLastPath,
  StoreInitialState,
  SetPanelMessage,
  StoreBasket,
  StoreBasketPriceRule,
  SelectUnSelectGroupOnBasket,
  SelectItemOnBasket,
  SelectItemsOnBasket,
  UnselectItemOnBasket,
  UnselectItemsOnBasket,
  UnselectAllItemsOnBasket,
  UpdateItemOnBasket,
  UpdateItemSettingsOnBasket,
  UpdateItemSettingsTgopOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemProductRootOnBasket,
  UpdateItemStatusOnBasket,
  UpdateItemRootOnBasket,
  UpdateItemManualTaskOnBasket,
  UpdateItemManualTaskFormFieldOnBasket,
  ApplyToAllOnBasket,
  ShowOnlyAvailableToggleBasket,
  SetBasketUnitApiCallLoading,
  BundleMakerAddComponent,
  BundleMakerRemoveComponent,
  BundleMakerAddComponentAsset,
  BundleMakerRemoveComponentAsset,
  BundleMakerRemoveComponentAssetAll,
  UpdateItemSettingsImfOnBasket,
} = basketSlice.actions;

// Basket main frame

export const GetBasket = (state): Basket => state.basket?.basket;

export const GetBasketItem =
  (itemId: number) =>
  (state): DbOrderBasketItemInfos =>
    state.basket.basket.items.find((x) => x.id === itemId);

export const GetBasketItemSettings =
  (itemId: number) =>
  (state): any =>
    state.basket.basket.items.find((x) => x.id === itemId).selectedProduct.settings;

export const GetBasketItemSettingsTgop =
  (itemId: number) =>
  (state): TranskodeGenericOptionsProfile =>
    state.basket.basket.items.find((x) => x.id === itemId).selectedProduct.settings.tgop;

export const GetBasketSelectedItems = (state): DbOrderBasketItemInfos[] =>
  state.basket.basketSelectedItemIds && state.basket.basketSelectedItemIds.length > 0 && state.basket.basket
    ? state.basket.basket.items.filter((x) => state.basket.basketSelectedItemIds.includes(x.id))
    : [];

export const GetBasketSelectedItemIds = (state): number[] => state.basket.basketSelectedItemIds;

export const GetBasketSelectedGroupId = (state): number | undefined => state.basket.basketSelectedGroupId;

export const GetItemPriceRule =
  (itemId: number) =>
  (state): BasketPriceRuleStore | undefined => {
    return state.basket.basketPriceRules.find((x) => x.itemId === itemId);
  };

export const BasketShowOnlyAvailable = (state): boolean => state.basket.basketShowOnlyAvailable;

export const IsBasketUnitApiCallLoading = (state): boolean => state.basket.basketUnitApiCallLoading;

// Panel
export const GetBasketItems = (state): BasketQuickviewItem[] => state.basket.items;
export const GetBasketProfiles = (state): XmlDeliveryIdentityProfilesInfo[] | null => state.basket.profiles;
export const GetBasketBundleProfiles = (state): XmlBundleDeliveryIdentityProfilesInfo[] | null =>
  state.basket.bundleProfiles;
export const GetBasketPanelMessage = (state): string | null => state.basket.message;
export const IsLoadingBasket = (state): boolean => state.basket.isLoading;
export const IsLoadedBasket = (state): boolean => state.basket.isLoaded;
export const GetLastPath = (state): string => state.basket.lastPathBeforeBasket;

export default basketSlice.reducer;
